import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import MediaComponent from '../Media/media-component.js';
import { ButtonPrimary } from '../Buttons/button-component.js';
import { getServicesData } from '../../Public/services-data';

const DRAG_THRESHOLD = 60;
const PLACEHOLDER_IMAGE = '/placeholder.svg';

/** Turn a service record into up to 3 short bullet strings for the slide. */
function serviceToBullets(svc) {
    if (Array.isArray(svc.highlights) && svc.highlights.length) return svc.highlights.slice(0, 3);
    if (Array.isArray(svc.bullets) && svc.bullets.length) return svc.bullets.slice(0, 3);
    const sec = (svc.sections || []).find((s) => Array.isArray(s.items) && s.items.length);
    if (sec) return sec.items.slice(0, 3).map((it) => it.title || it).filter(Boolean);
    const fallback = svc.card?.description || svc.subHeadline || svc.description;
    return fallback ? [fallback] : [];
}

/** Map a service record into the slide shape this carousel renders. */
function serviceToSlide(svc) {
    return {
        title: svc.card?.title || svc.cardTitle || svc.headline || svc.name || '',
        // Short blurb about the service (avoid the generic hero subtitle).
        desc: svc.card?.description || svc.cardDescription || svc.subHeadline || svc.description || '',
        href: svc.actionHref || (svc.slug ? `/services/${svc.slug}` : '#'),
        // The carousel uses the service's dedicated mascot/character image (set in
        // servicesData), NOT its situational photo, falls back to the photo only
        // if a service has no mascot yet.
        image: svc.mascotImage || svc.card?.mascotImage || svc.card?.image || svc.cardImage || svc.image || svc.media || svc.hero?.imageDesktop,
        items: serviceToBullets(svc),
    };
}

export default function TrustSection({ trustSection }) {
    const { dir = 'rtl', language = 'he' } = useSelector((state) => state.systemData || {});

    const {
        headingLine1 = 'Clear problems.',
        headingLine2 = 'Move forward.',
        description  = 'We handle the most common permit and violation issues for property owners in South Florida. Click or scroll to explore.',
        ctaText      = 'See All Services',
        ctaHref      = '/services',
        slideCtaText = 'Learn More',
    } = trustSection || {};

    // Carousel content comes from the real services, one slide per service.
    const [services, setServices] = useState([]);
    useEffect(() => {
        let alive = true;
        getServicesData(language)
            .then((list) => { if (alive) setServices(Array.isArray(list) ? list : []); })
            .catch(() => { if (alive) setServices([]); });
        return () => { alive = false; };
    }, [language]);

    const points = useMemo(() => {
        const active = (services || []).filter((s) => s.isActive !== false);
        const mapped = active.map(serviceToSlide);
        // Fall back to any hardcoded points only if services haven't loaded yet.
        return mapped.length ? mapped : (trustSection?.points || []);
    }, [services, trustSection]);

    const [activeIndex, setActiveIndex] = useState(0);
    const [dragPx, setDragPx] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [paused, setPaused] = useState(false); // stops autoplay once the user interacts
    const startX = useRef(null);
    const viewportRef = useRef(null);
    const total = points.length;

    // Keep the active index in range if the number of services changes.
    useEffect(() => {
        setActiveIndex((i) => (total ? Math.min(i, total - 1) : 0));
    }, [total]);

    const prev = useCallback(() => {
        if (!total) return;
        setActiveIndex(i => (i - 1 + total) % total);
    }, [total]);

    const next = useCallback(() => {
        if (!total) return;
        setActiveIndex(i => (i + 1) % total);
    }, [total]);

    // auto-rotate every 3s until the user touches/interacts
    useEffect(() => {
        if (paused || total <= 1) return;
        const id = setInterval(() => next(), 3000);
        return () => clearInterval(id);
    }, [paused, total, next]);


    const onPointerDown = (e) => { setPaused(true); startX.current = e.clientX; setIsDragging(true); };
    const onPointerMove = (e) => {
        if (startX.current === null) return;
        setDragPx(e.clientX - startX.current);
    };
    const finishDrag = (clientX) => {
        if (startX.current === null) return;
        const delta = clientX - startX.current;
        startX.current = null;
        setIsDragging(false);
        setDragPx(0);
        if (Math.abs(delta) > DRAG_THRESHOLD) {
            const goNext = dir === 'rtl' ? delta > 0 : delta < 0;
            goNext ? next() : prev();
        }
    };

    const active = points[activeIndex] || {};
    const vpWidth = viewportRef.current?.offsetWidth || 1;
    const basePct = -activeIndex * 100;
    const dragPct = (dragPx / vpWidth) * 100;

    const TitleBlock = ({ centered = false }) => (
        <div className={centered ? 'text-center' : 'text-start'}>
            <h3 className="font-secondary font-extrabold text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-[-0.02em] text-primary-500">
                {active.title}
            </h3>
        </div>
    );

    return (
        <section dir={dir} className="relative w-full bg-primary-50 py-5 md:py-28 px-4 md:px-8 overflow-hidden">
            <div className="relative z-10 max-w-[1520px] mx-auto flex flex-col lg:grid lg:grid-cols-12 gap-5 lg:gap-6 items-center">

                {/* LEFT, static intro */}
                <div className="order-1 lg:order-none lg:col-span-3 text-center lg:text-start">
                    <h2 className="font-secondary font-extrabold leading-[0.92] tracking-[-0.03em] mt-4 md:mt-5 text-5xl md:text-6xl xl:text-7xl">
                        <span className="block text-primary-500">{headingLine1}</span>
                        <span className="block text-secondary-500">{headingLine2}</span>
                    </h2>
                    <p className="font-primary mt-5 md:mt-7 text-lg md:text-xl text-primary-300 leading-relaxed max-w-md mx-auto lg:mx-0">
                        {description}
                    </p>
                    <div className="mt-7 md:mt-9 flex justify-center lg:justify-start">
                        <ButtonPrimary size="lg" href={ctaHref} className="!w-auto !px-8">
                            {dir === 'rtl' ? `${ctaText} ←` : `${ctaText} →`}
                        </ButtonPrimary>
                    </div>
                </div>

                {/* MOBILE-ONLY, slide title above the image */}
                {total > 0 && (
                    <div key={`mt-${activeIndex}`} className="order-2 lg:hidden w-full cv-trust-fade">
                        <TitleBlock centered />
                    </div>
                )}

                {/* CENTER, big image sliding sideways, never cropped */}
                <div className="order-3 lg:order-none lg:col-span-6 w-full">
                    <div
                        ref={viewportRef}
                        dir="ltr"
                        className="relative w-full aspect-square md:aspect-[5/4] overflow-hidden cursor-grab active:cursor-grabbing select-none"
                        style={{ touchAction: 'pan-y' }}
                        onPointerDown={onPointerDown}
                        onPointerMove={onPointerMove}
                        onPointerUp={e => finishDrag(e.clientX)}
                        onPointerLeave={e => finishDrag(e.clientX)}
                        onPointerCancel={() => { startX.current = null; setIsDragging(false); setDragPx(0); }}
                        onDragStart={e => e.preventDefault()}
                    >
                        <div
                            className="flex h-full"
                            style={{
                                transform: `translateX(${basePct + dragPct}%)`,
                                transition: isDragging ? 'none' : 'transform 0.6s cubic-bezier(0.22,1,0.36,1)',
                            }}
                        >
                            {points.map((card, i) => (
                                <div
                                    key={i}
                                    className="shrink-0 w-full h-full flex items-center justify-center"
                                >
                                    <MediaComponent
                                        src={card.image || PLACEHOLDER_IMAGE}
                                        alt={card.title}
                                        quality={60}
                                        width={600}
                                        objectFit="contain"
                                        wrapperClassName="w-full h-full [&_img]:pointer-events-none [&_img]:select-none"
                                        useSekeleton={false}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* overlay arrows on the image sides (all breakpoints) */}
                        <button
                            type="button"
                            onClick={prev}
                            aria-label="Previous"
                            className="absolute left-2 md:left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center text-primary-500 hover:text-secondary-500 active:scale-95 transition-all"
                        >
                            <ChevronLeft className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2.5} />
                        </button>
                        <button
                            type="button"
                            onClick={next}
                            aria-label="Next"
                            className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/90 backdrop-blur shadow-lg flex items-center justify-center text-primary-500 hover:text-secondary-500 active:scale-95 transition-all"
                        >
                            <ChevronRight className="w-6 h-6 md:w-7 md:h-7" strokeWidth={2.5} />
                        </button>
                    </div>
                </div>

                {/* RIGHT / BELOW, details. Title shows here on desktop only. */}
                {total > 0 && (
                    <div className="order-4 lg:col-span-3 w-full">
                        <div key={activeIndex} className="cv-trust-fade text-center lg:text-start">
                            <div className="hidden lg:block">
                                <TitleBlock />
                            </div>
                            {active.desc && (
                                <p className="font-primary mt-3 lg:mt-4 text-base md:text-lg text-primary-300 leading-relaxed max-w-md mx-auto lg:mx-0">
                                    {active.desc}
                                </p>
                            )}
                            <ul className="mt-3 lg:mt-6 space-y-3 md:space-y-3.5 inline-block text-start">
                                {(active.items || []).map((item, j) => (
                                    <li key={j} className="flex items-start gap-3 font-primary text-base md:text-lg text-primary-400">
                                        <span className="mt-2.5 w-2 h-2 rounded-full bg-secondary-500 shrink-0" />
                                        <span className="flex-1">{item}</span>
                                    </li>
                                ))}
                            </ul>
                            {active.href && active.href !== '#' && (
                                <div className="mt-5 md:mt-8 flex justify-center lg:justify-start">
                                    <ButtonPrimary
                                        href={active.href}
                                        size="lg"
                                        className="!w-auto !px-7"
                                        icon={<ArrowRight className="w-5 h-5" strokeWidth={2.5} />}
                                    >
                                        {slideCtaText}
                                    </ButtonPrimary>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <style>{`
                @keyframes cvTrustFade {
                    from { opacity: 0; transform: translateY(14px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                .cv-trust-fade { animation: cvTrustFade 0.5s cubic-bezier(0.22,1,0.36,1) both; }
            `}</style>
        </section>
    );
}
